import { LayoutModule } from '@angular/cdk/layout';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { InMemoryWebApiModule } from 'angular-in-memory-web-api';
import { CustomRouteReuseStategy } from 'global/custom-route-reuse-strategy';
import { AppComponent } from './app.component';
import { rootRouterConfig } from './app.routing';
import {
  PERFECT_SCROLLBAR_CONFIG,
  PerfectScrollbarConfigInterface,
  PerfectScrollbarModule,
} from './shared/components/perfect-scrollbar';
import { InMemoryDataService } from './shared/inmemory-db/inmemory-db.service';
import { ApiAuthInterceptor } from './shared/interceptors/api-auth.interceptor';
import { EncryptionInterceptor } from './shared/interceptors/encryption.interceptor';
import { ForbiddenResponseInterceptor } from './shared/interceptors/forbidden-response.interceptor';
import { AuthService } from './shared/services/auth/Auth.service';
import { ErrorHandlerService } from './shared/services/error-handler.service';
import { KeycloakAuthService } from './shared/services/keycloak/keycloak-auth.service';
import { LoaderInterceptor } from './shared/services/loader.interceptor';
import { NavigationService } from './shared/services/navigation.service';
import { SharedModule } from './shared/shared.module';

// AoT requires an exported function for factories
export function HttpLoaderFactory(httpClient: HttpClient) {
  // return new TranslateHttpLoader(httpClient);
  return new TranslateHttpLoader(httpClient, 'assets/i18n/', '.json');
}

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true,
};
const APP_BOOTSTRAP_AUTH_TIMEOUT_MS = 8000;

const initializeKeycloak =
  (
    keycloakAuthService: KeycloakAuthService,
    navigationService: NavigationService,
  ) =>
  async () => {
    const timeoutPromise = new Promise<boolean>((resolve) => {
      setTimeout(() => {
        console.warn(
          'Keycloak initialization timed out; continuing app bootstrap.',
        );
        resolve(false);
      }, APP_BOOTSTRAP_AUTH_TIMEOUT_MS);
    });

    try {
      const isAuthenticated = await Promise.race([
        keycloakAuthService.initialize(),
        timeoutPromise,
      ]);

      if (isAuthenticated) {
        await navigationService.initialize(true);
      }
    } catch (error) {
      console.error('Keycloak initialization failed', error);
    }

    return true;
  };
@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    SharedModule,
    LayoutModule,
    PerfectScrollbarModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    InMemoryWebApiModule.forRoot(InMemoryDataService, {
      passThruUnknownUrl: true,
    }),
    RouterModule.forRoot(rootRouterConfig, { useHash: false }),
    NgIdleKeepaliveModule.forRoot(),
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      deps: [KeycloakAuthService, NavigationService],
      multi: true,
    },
    provideClientHydration(withEventReplay()),

    { provide: ErrorHandler, useClass: ErrorHandlerService },
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
    },

    AuthService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiAuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: EncryptionInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ForbiddenResponseInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStategy },
  ],
})
export class AppModule {}
